import { forwardRef, useImperativeHandle, useRef } from "react";

import { Box, Tabs } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { displayClasses } from "styles";
import { CreditOptionsResponse, CreditResponse } from "types";

import { CreditComparisonSource } from "./request/comparison-source";
import { CreditRequestModeProvider } from "./request/request-mode-context";
import CreditDocumentation from "./documentation";
import CreditPayment from "./payment";
import CreditRequest, { CreditRequestHandle } from "./request";

const TABS = {
  REQUEST: "request",
  DOCUMENTATION: "documentation",
  PAYMENT: "payment",
};

const PAYMENT_LOCKED_STATUSES = [
  "DRAFT",
  "NEW_REQUEST",
  "INCOMPLETE_DOCUMENTATION",
  "PROCESSING",
] as const;

interface CreditTabsProps {
  credit: CreditResponse;
  options: CreditOptionsResponse;
  readOnly?: boolean;
  isPreliminary?: boolean;
  updateService?: (id: number, data: any) => Promise<void>;
  comparisonSource?: CreditComparisonSource | null;
}

export interface CreditTabsHandle {
  hasMissingRequiredFields: () => boolean;
}

const CreditTabs = forwardRef<CreditTabsHandle, CreditTabsProps>(
  (
    { credit, options, readOnly = false, isPreliminary = false, updateService, comparisonSource },
    ref,
  ) => {
    const { t } = useTranslation();
    const requestRef = useRef<CreditRequestHandle>(null);

    useImperativeHandle(ref, () => ({
      hasMissingRequiredFields: () => requestRef.current?.hasMissingRequiredFields() ?? true,
    }));

    return (
      <Box>
        <Tabs defaultValue={TABS.REQUEST} keepMounted={!isPreliminary} variant={"pills"} mt={"lg"}>
          <Tabs.List>
            <Tabs.Tab value={TABS.REQUEST} className={displayClasses.tabList}>
              {t(isPreliminary ? "credit.view.tab.preliminary" : "credit.view.tab." + TABS.REQUEST)}
            </Tabs.Tab>
            <Tabs.Tab
              value={TABS.DOCUMENTATION}
              disabled={!credit.requestedAt}
              className={displayClasses.tabList}
            >
              {t("credit.view.tab." + TABS.DOCUMENTATION)}
            </Tabs.Tab>
            <Tabs.Tab
              value={TABS.PAYMENT}
              disabled={PAYMENT_LOCKED_STATUSES.includes(
                credit.status as (typeof PAYMENT_LOCKED_STATUSES)[number],
              )}
              className={displayClasses.tabList}
            >
              {t("credit.view.tab." + TABS.PAYMENT)}
            </Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value={TABS.REQUEST} className={displayClasses.tabContent}>
            <CreditRequestModeProvider isPreliminary={isPreliminary}>
              <CreditRequest
                ref={requestRef}
                credit={credit}
                options={options}
                readOnly={readOnly}
                updateService={updateService}
                comparisonSource={comparisonSource}
              />
            </CreditRequestModeProvider>
          </Tabs.Panel>
          <Tabs.Panel value={TABS.DOCUMENTATION} className={displayClasses.tabContent}>
            <CreditDocumentation credit={credit} />
          </Tabs.Panel>
          <Tabs.Panel value={TABS.PAYMENT} className={displayClasses.tabContent}>
            <CreditPayment options={options} credit={credit} readOnly={readOnly} />
          </Tabs.Panel>
        </Tabs>
      </Box>
    );
  },
);

export default CreditTabs;
